import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { Link, RowTypes, SnipPathToDataMap, Sort } from '../types';
import { SnippetPathTree } from './getSnippetPathTree';

const getSortField = (sortBy: string, type: RowTypes, object: { [key: string]: any }) => {
  if (sortBy === 'name') {
    if (type === RowTypes.Snippet || type === RowTypes.Combined || type === RowTypes.Campaign) {
      return object.name;
    } else if (type === RowTypes.Link) {
      return object.templateUrl;
    } else {
      return object.url;
    }
  } else if (sortBy === 'TotalSent') {
    if (type === RowTypes.Snippet || type === RowTypes.Campaign) {
      return object.metrics.TotalSent;
    } else {
      return object.metrics.TotalClicks;
    }
  } else if (sortBy === 'TotalClicks') {
    return object.metrics.TotalClicks;
  } else if (sortBy === 'UniqueClicks') {
    return object.metrics.UniqueClicks;
  } else {
    if (type === RowTypes.Snippet || type === RowTypes.Campaign) {
      return object.metrics.ClickRate;
    }
    return object.metrics.TotalClicks;
  }
};

const getSortFn = (sortBy: string, firstVal: string | number, secondVal: string | number) => {
  if (sortBy === 'name') {
    return (firstVal as string)
      .toLocaleLowerCase()
      .localeCompare((secondVal as string).toLocaleLowerCase());
  }
  return (firstVal as number) - (secondVal as number);
};

export const sortRows = (rows: any[], sort: Sort, type: RowTypes) => {
  const { sortBy, sortDirection } = sort;
  const sortedRows = rows.slice().sort((a, b) => {
    const valA = getSortField(sortBy, type, a);
    const valB = getSortField(sortBy, type, b);

    const firstVal = sortDirection === SORT_DIRECTIONS.ASCENDING ? valA : valB;
    const secondVal = sortDirection === SORT_DIRECTIONS.ASCENDING ? valB : valA;

    return getSortFn(sortBy, firstVal, secondVal);
  });
  return sortedRows;
};

export const getCombinedSnippetAndLinkIdOrder = (
  linkData: Link[],
  children: SnippetPathTree[],
  snipPathToDataMap: SnipPathToDataMap,
  sort: Sort,
): number[] => {
  const { sortBy, sortDirection } = sort;
  const snipData = children.map((snip) => snipPathToDataMap[snip.path]);
  const updatedLinkData = linkData.map((link) => ({
    ...link,
    name: link.templateUrl,
    metrics: { ...link.metrics, TotalSent: 0, ClickRate: 0 },
  }));
  const combinedData = [...snipData, ...updatedLinkData];

  const getIds = (i: any) => i.snippetPath || i.id;

  if (sortBy === 'name') {
    return sortRows(combinedData, sort, RowTypes.Combined).map(getIds);
  } else if (sortBy === 'TotalSent' || sortBy === 'ClickRate') {
    const sortedSnippets = sortRows(snipData, sort, RowTypes.Snippet).map(getIds);
    const sortedLinks = sortRows(updatedLinkData, sort, RowTypes.Link).map(getIds);
    return sortDirection === SORT_DIRECTIONS.ASCENDING
      ? [...sortedLinks, ...sortedSnippets]
      : [...sortedSnippets, ...sortedLinks];
  } else {
    return sortRows(combinedData, sort, RowTypes.Combined).map(getIds) as number[];
  }
};
